import { useEffect, useState } from "react";
import { groupKeyId, listGrantGroups, listGrantViewers } from "../../lib/accessClient.js";
import { ACTIVITY_CAPABILITIES } from "../../lib/activityConfiguration.js";
import Button from "../ui/Button.jsx";
import SearchInput from "../ui/SearchInput.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import { FIELD, MENU_ITEM_ON } from "../ui/styles.js";

export const INPUT = FIELD;
/// A quiet line of explanation under or beside a control.
export const NOTE = "m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]";
export const ALL_ACTIVITY = "All tenant activity, including former employees and unmapped records";
export const labelFor = capability => ACTIVITY_CAPABILITIES.find(item => item.value === capability)?.label ?? "Unknown capability";
const PROVIDERS = { okta: "Okta", entra_id: "Entra ID", google_workspace: "Google Workspace" };
export const groupName = group => `${group.displayName || "Directory group (name unavailable)"} (${PROVIDERS[group.sourceProvider] || group.sourceProvider})`;
export const recipientName = recipient => recipient?.viewerUserId
    ? recipient.displayName || "Individual user (name unavailable)" : recipient?.directoryGroup ? groupName(recipient.directoryGroup) : "Unknown recipient";
export const scopeName = scope => scope?.allTenantActivity ? ALL_ACTIVITY
    : scope?.groups ? `Selected groups: ${scope.groups.groups.map(groupName).join(", ")}` : "Approved investigation target";
export const shortLabelFor = capability => ACTIVITY_CAPABILITIES.find(item => item.value === capability)?.short ?? "Unknown capability";
/// Groups sharing one recipient collapse under a single heading, so the key has
/// to separate a viewer from a directory group that happens to share an id.
export const recipientKey = recipient => recipient?.viewerUserId ? `viewer:${recipient.viewerUserId}`
    : recipient?.directoryGroup ? `group:${groupKeyId(recipient.directoryGroup)}` : "unknown";
/// A list row states the scope, not the whole membership. Names past `limit`
/// collapse to a count rather than wrapping the row over several lines. The
/// provider is dropped here: it repeats on every group in the row while the
/// recipient heading above still carries it.
export function compactScope(scope, limit = 3) {
    if (scope?.allTenantActivity) return "All tenant activity";
    const names = scope?.groups?.groups?.map(group => group.displayName || "Directory group (name unavailable)") ?? [];
    if (!names.length) return "Approved investigation target";
    return names.length <= limit ? names.join(", ") : `${names.slice(0, limit).join(", ")} +${names.length - limit}`;
}

export function CandidatePicker({ kind, title, selected, onSelect }) {
    const [search, setSearch] = useState("");
    const [pageToken, setPageToken] = useState("");
    const [result, setResult] = useState(null);
    const [retry, setRetry] = useState(0);
    const query = JSON.stringify([kind, search, pageToken, retry]);
    const page = result?.query === query ? result : null;
    useEffect(() => {
        let current = true;
        const timer = setTimeout(() => {
            const load = kind === "viewer" ? listGrantViewers : listGrantGroups;
            load({ search, pageToken, pageSize: 25, ...(kind === "viewer" ? {} : { primaryDirectoryOnly: true }) }).then(value => {
                if (current) setResult({ query, rows: value.viewers ?? value.groups, next: value.nextPageToken, degraded: value.degraded });
            }).catch(() => { if (current) setResult({ query, rows: [], error: true }); });
        }, 180);
        return () => { current = false; clearTimeout(timer); };
    }, [query, kind, search, pageToken]);
    return <div className="grid gap-[8px]" aria-busy={!page}>
        <div className="grid gap-[6px]"><span className="text-[12.5px] font-medium text-[color:var(--ink)]">{title}</span>
            <SearchInput value={search} onChange={value => { setSearch(value); setPageToken(""); }} placeholder="" label={title} />
        </div>
        {kind !== "viewer" ? <p className={NOTE}>Primary directory only</p> : null}
        {!page ? <p className={NOTE}>Loading choices...</p> : null}
        {page?.error ? <div className="flex flex-wrap items-center gap-[8px]"><ErrorText>Choices could not be loaded.</ErrorText><Button size="sm" onClick={() => setRetry(value => value + 1)}>Retry choices</Button></div> : null}
        {page?.degraded ? <p role="status" className={NOTE}>Directory groups are unavailable. Try again after directory access is restored.</p> : null}
        {page && !page.error && !page.degraded && !page.rows.length ? <p className={NOTE}>No matching {kind === "viewer" ? "users" : "groups in the primary directory"}.</p> : null}
        <div className="grid max-h-60 gap-[2px] overflow-y-auto">{page?.rows.map(row => {
            const id = kind === "viewer" ? row.viewerUserId : groupKeyId(row);
            const chosen = selected.has(id);
            return <button key={id} type="button" aria-pressed={chosen} onClick={() => onSelect(row)}
                className={`flex min-w-0 cursor-pointer items-center justify-between gap-[8px] rounded-[6px] border-0 bg-transparent px-[10px] py-[7px] text-left text-[13px] text-[color:var(--ink)] transition-colors hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] disabled:cursor-not-allowed disabled:opacity-50 ${chosen ? MENU_ITEM_ON : ""}`}>
                <span className="min-w-0 truncate">{kind === "viewer" ? row.displayName || "Individual user (name unavailable)" : groupName(row)}</span>
                {chosen ? <svg aria-hidden="true" viewBox="0 0 12 12" className="h-[12px] w-[12px] shrink-0 text-[color:var(--green)]"><path d="M2.5 6.5 5 9l4.5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg> : null}
            </button>;
        })}</div>
        {pageToken || page?.next ? <div className="flex gap-[8px]">
            {pageToken ? <Button size="sm" onClick={() => setPageToken("")}>First choices</Button> : null}
            {page?.next ? <Button size="sm" onClick={() => setPageToken(page.next)}>Next choices</Button> : null}
        </div> : null}
    </div>;
}

export function keyOnly(group) {
    return { sourceProvider: group.sourceProvider, integrationId: group.integrationId, sourceId: group.sourceId };
}
